import { useState, useEffect } from 'react';
import {
  MessageSquare,
  Check,
  X,
  Search,
  Filter,
  AlertTriangle,
  ThumbsUp,
  ChevronDown,
  ChevronUp,
  Trash2,
  RotateCcw,
  RefreshCw
} from 'lucide-react';
import { adminCommentsApi } from '../utils/apiService';
import { toast } from 'sonner';

interface ReportedComment {
  id: string;
  commentText: string;
  author: {
    name: string;
    avatar: string;
  };
  thoughtTitle: string;
  reportedBy: string | null;
  reportReason: string | null;
  reportDate: string;
  status: 'pending' | 'approved' | 'removed';
  likes: number;
  replies: number;
  repliesData: Array<{ id: string; text: string; author: string; avatar: string; likes: number; createdAt: string }>;
}

export default function CommentModeration() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'removed'>('all');
  const [comments, setComments] = useState<ReportedComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const toggleReplies = (id: string) => {
    setExpandedReplies(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      const params: any = { limit: 50, page: 1 };
      if (filterStatus !== 'all') params.status = filterStatus === 'removed' ? 'rejected' : filterStatus;

      const result = await adminCommentsApi.getAll(params);

      const mappedComments = result.comments.map((comment: any) => ({
        id: comment._id,
        commentText: comment.text || comment.content || '',
        author: {
          name: comment.author?.name || comment.userId?.name || 'Anonymous',
          avatar: comment.author?.avatar || comment.userId?.avatar || '👤'
        },
        thoughtTitle: comment.thoughtId?.text || comment.thought?.text || 'Unknown Thought',
        reportedBy: comment.reportedBy || null,
        reportReason: comment.reportReason || null,
        reportDate: formatDate(comment.createdAt),
        status: comment.moderationStatus === 'rejected' ? 'removed' : (comment.moderationStatus || 'pending'),
        likes: comment.likes || 0,
        replies: comment.replies?.length || 0,
        repliesData: (comment.replies || []).map((r: any) => ({
          id: r._id,
          text: r.text || r.content || '',
          author: r.userId?.name || r.author?.name || 'Anonymous',
          avatar: r.userId?.avatar || r.author?.avatar || '👤',
          likes: r.likes || 0,
          createdAt: formatDate(r.createdAt)
        }))
      }));

      setComments(mappedComments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [filterStatus]);

  const handleApprove = async (id: string) => {
    setActionLoading(id + '_approve');
    try {
      const result = await adminCommentsApi.approve(id);
      if (result.success) {
        setComments(prev => prev.map(c => c.id === id ? { ...c, status: 'approved' as const } : c));
        toast.success('Comment approved');
      } else {
        toast.error('Failed to approve comment');
      }
    } catch {
      toast.error('Failed to approve comment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (id: string) => {
    setActionLoading(id + '_remove');
    try {
      const result = await adminCommentsApi.remove(id);
      if (result.success) {
        setComments(prev => prev.map(c => c.id === id ? { ...c, status: 'removed' as const } : c));
        toast.success('Comment removed');
      } else {
        toast.error('Failed to remove comment');
      }
    } catch {
      toast.error('Failed to remove comment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently delete this comment? This cannot be undone.')) return;
    setActionLoading(id + '_delete');
    try {
      await adminCommentsApi.delete(id);
      setComments(prev => prev.filter(c => c.id !== id));
      toast.success('Comment permanently deleted');
    } catch {
      toast.error('Failed to delete comment');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredComments = comments.filter(comment => {
    const matchesSearch =
      comment.commentText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comment.author.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comment.thoughtTitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || comment.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    pending: comments.filter(c => c.status === 'pending').length,
    approved: comments.filter(c => c.status === 'approved').length,
    removed: comments.filter(c => c.status === 'removed').length,
    total: comments.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">Comment Moderation</h2>
          <p className="text-slate-500 text-sm">Review, approve, or remove user comments</p>
        </div>
        <button
          onClick={fetchComments}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm text-slate-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards — clickable as filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', count: stats.total, filter: 'all' as const, color: 'slate', icon: MessageSquare },
          { label: 'Pending', count: stats.pending, filter: 'pending' as const, color: 'amber', icon: AlertTriangle },
          { label: 'Approved', count: stats.approved, filter: 'approved' as const, color: 'green', icon: Check },
          { label: 'Removed', count: stats.removed, filter: 'removed' as const, color: 'red', icon: X },
        ].map(({ label, count, filter, color, icon: Icon }) => (
          <button
            key={filter}
            onClick={() => setFilterStatus(filter)}
            className={`text-left bg-white rounded-xl p-4 border-2 transition-all hover:shadow-md ${
              filterStatus === filter
                ? `border-${color}-400 shadow-sm`
                : 'border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm text-${color}-600 font-medium`}>{label}</p>
                <p className={`text-2xl font-bold text-${color}-800`}>{count}</p>
              </div>
              <Icon className={`w-8 h-8 text-${color}-400`} />
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by comment, author, or thought..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2.5 border border-slate-200 rounded-xl bg-white focus:outline-none focus:border-[#2B7A7E] cursor-pointer text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="removed">Removed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <RefreshCw className="w-8 h-8 text-slate-400 mx-auto mb-3 animate-spin" />
            <p className="text-slate-500 text-sm">Loading comments...</p>
          </div>
        ) : filteredComments.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-700 mb-1">No comments found</h3>
            <p className="text-slate-500 text-sm">Try adjusting your search or filter</p>
          </div>
        ) : (
          filteredComments.map(comment => (
            <div
              key={comment.id}
              className={`bg-white rounded-xl p-5 border-l-4 border border-slate-200 transition-all ${
                comment.status === 'pending' ? 'border-l-amber-400' :
                comment.status === 'approved' ? 'border-l-green-400' :
                'border-l-red-400'
              }`}
            >
              {/* Top row: status badge + date + delete */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {comment.status === 'pending' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                      <AlertTriangle className="w-3 h-3" /> Pending
                    </span>
                  )}
                  {comment.status === 'approved' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                      <Check className="w-3 h-3" /> Approved
                    </span>
                  )}
                  {comment.status === 'removed' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                      <X className="w-3 h-3" /> Removed
                    </span>
                  )}
                  {comment.reportReason && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">
                      <AlertTriangle className="w-3 h-3" /> Reported
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{comment.reportDate}</span>
                  <button
                    onClick={() => handleDelete(comment.id)}
                    disabled={actionLoading === comment.id + '_delete'}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete permanently"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Comment text */}
              <div className="bg-slate-50 rounded-lg px-4 py-3 mb-3 border border-slate-100">
                <p className="text-slate-700 text-sm leading-relaxed">"{comment.commentText}"</p>
              </div>

              {/* Thought context */}
              <p className="text-xs text-slate-500 mb-3">
                On: <span className="font-medium text-[#2B7A7E]">"{comment.thoughtTitle}"</span>
              </p>

              {/* Author + stats */}
              <div className="flex items-center gap-5 text-xs text-slate-500 mb-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-base">{comment.author.avatar}</span>
                  <span className="font-medium text-slate-700">{comment.author.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ThumbsUp className="w-3.5 h-3.5" />
                  <span>{comment.likes} likes</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>{comment.replies} {comment.replies === 1 ? 'reply' : 'replies'}</span>
                </div>
              </div>

              {/* Replies expandable */}
              {comment.replies > 0 && (
                <div className="mb-3">
                  <button
                    onClick={() => toggleReplies(comment.id)}
                    className="flex items-center gap-1.5 text-xs font-medium text-[#2B7A7E] hover:text-[#1e5a5d] transition-colors"
                  >
                    {expandedReplies.has(comment.id) ? (
                      <><ChevronUp className="w-3.5 h-3.5" /> Hide replies</>
                    ) : (
                      <><ChevronDown className="w-3.5 h-3.5" /> View all {comment.replies} {comment.replies === 1 ? 'reply' : 'replies'}</>
                    )}
                  </button>

                  {expandedReplies.has(comment.id) && (
                    <div className="mt-2 space-y-2 pl-4 border-l-2 border-slate-200">
                      {comment.repliesData.length > 0 ? (
                        comment.repliesData.map(reply => (
                          <div key={reply.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-base">{reply.avatar}</span>
                                <span className="text-xs font-medium text-slate-700">{reply.author}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{reply.likes}</span>
                                <span>{reply.createdAt}</span>
                              </div>
                            </div>
                            <p className="text-xs text-slate-600">"{reply.text}"</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400 italic py-1">Reply content not available</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Report reason — only shown when backend has actual report data */}
              {comment.reportReason && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-orange-800">Report reason: <span className="font-normal">{comment.reportReason}</span></p>
                    {comment.reportedBy && (
                      <p className="text-xs text-orange-600 mt-0.5">Reported by: {comment.reportedBy}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                {comment.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(comment.id)}
                      disabled={actionLoading === comment.id + '_approve'}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors text-sm font-medium disabled:opacity-60"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRemove(comment.id)}
                      disabled={actionLoading === comment.id + '_remove'}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors text-sm font-medium disabled:opacity-60"
                    >
                      <X className="w-4 h-4" />
                      Remove
                    </button>
                  </>
                )}

                {comment.status === 'approved' && (
                  <button
                    onClick={() => handleRemove(comment.id)}
                    disabled={actionLoading === comment.id + '_remove'}
                    className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium disabled:opacity-60"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                )}

                {comment.status === 'removed' && (
                  <button
                    onClick={() => handleApprove(comment.id)}
                    disabled={actionLoading === comment.id + '_approve'}
                    className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-green-50 hover:text-green-600 transition-colors text-sm font-medium disabled:opacity-60"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Restore
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary */}
      {!isLoading && filteredComments.length > 0 && (
        <p className="text-center text-xs text-slate-400">
          Showing {filteredComments.length} of {comments.length} comments
        </p>
      )}
    </div>
  );
}
